import { useDevice } from '@/hooks';
import { PageSpinner } from '@/components/common/Spinner/Spinner';
import { StatusBadge } from '@/components/common/StatusBadge/StatusBadge';
import { getProductCapabilities } from '@/lib/product-capabilities';

function InfoRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-start justify-between gap-4 py-2">
      <span className="shrink-0 text-sm text-slate-500 dark:text-slate-400">{label}</span>
      <span className="text-right text-sm font-medium text-slate-800 dark:text-slate-200">{value ?? '—'}</span>
    </div>
  );
}

function InfoCard({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white dark:border-navy-700 dark:bg-navy-900">
      <div className="border-b border-slate-200 px-5 py-3 dark:border-navy-700">
        <h2 className="text-base font-semibold text-slate-800 dark:text-white">{title}</h2>
      </div>
      <div className="divide-y divide-slate-50 px-5 dark:divide-navy-800">{children}</div>
    </div>
  );
}

export function CommunicationsPage() {
  const { selectedDeviceInfo, deviceInfoLoading } = useDevice();

  if (deviceInfoLoading) return <PageSpinner />;

  if (!selectedDeviceInfo) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white">Communications</h1>
          <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">Network and message delivery status</p>
        </div>
        <div className="rounded-xl border border-dashed border-slate-300 bg-white p-12 text-center dark:border-navy-600 dark:bg-navy-900">
          <p className="text-sm text-slate-400 dark:text-slate-500">No device selected.</p>
        </div>
      </div>
    );
  }

  const dp = selectedDeviceInfo.deviceProperties;
  const mtList = selectedDeviceInfo.mtObjList ?? [];
  const caps = getProductCapabilities(dp?.deviceType);

  // A message is pending if it hasn't been received or aborted.
  // Real API uses boolean flags; mock data uses statusStr fallback.
  const pendingMessages = mtList.filter(
    (m) =>
      m.messageReceived !== undefined
        ? !m.messageReceived && !m.messageAborted
        : m.statusStr === 'PENDING' || m.statusStr === 'QUEUED',
  );
  const pendingCount = pendingMessages.length;

  const isOnline = dp?.isDirectConnected || dp?.isIoTCoreConnected;
  const showIridium = caps.supportsSatellite && !isOnline;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-slate-900 dark:text-white">Communications</h1>
        <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">Network and message delivery status</p>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        {/* Network */}
        <InfoCard title="Network">
          {/* 1. Status — always first */}
          <InfoRow
            label="Status"
            value={
              isOnline ? (
                <StatusBadge
                  label={dp?.isIoTCoreConnected ? 'Cloud Connected' : 'Direct Connected'}
                  variant="success"
                  dot
                />
              ) : (
                <StatusBadge label="Satellite Only" variant="warning" dot />
              )
            }
          />
          {/* 2. Carrier — when connected via cellular */}
          {dp?.networkCarrierStr && (
            <InfoRow label="Carrier" value={dp.networkCarrierStr.trim()} />
          )}
          {/* 3. Iridium — when not online and device has satellite */}
          {showIridium && (
            <InfoRow label="Network" value="Iridium Satellite" />
          )}
        </InfoCard>

        {/* Pending Messages */}
        <InfoCard title="Pending Messages">
          <div className="py-4">
            {pendingCount === 0 ? (
              <div className="flex flex-col items-center gap-2 py-2 text-center">
                {/* Checkmark circle */}
                <div className="flex h-12 w-12 items-center justify-center rounded-full bg-emerald-100 dark:bg-emerald-900/30">
                  <svg className="h-6 w-6 text-emerald-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <p className="text-sm font-medium text-slate-700 dark:text-slate-300">All messages delivered</p>
                <p className="text-xs text-slate-500 dark:text-slate-400">No commands are waiting to reach your device.</p>
              </div>
            ) : (
              <div className="flex flex-col items-center gap-2 py-2 text-center">
                {/* Badge with count */}
                <div className="flex h-12 w-12 items-center justify-center rounded-full bg-amber-100 dark:bg-amber-900/30">
                  <span className="text-xl font-bold text-amber-600 dark:text-amber-400">{pendingCount}</span>
                </div>
                <p className="text-sm font-medium text-slate-700 dark:text-slate-300">
                  {pendingCount === 1
                    ? '1 message waiting to be delivered'
                    : `${pendingCount} messages waiting to be delivered`}
                </p>
                <p className="text-xs text-slate-500 dark:text-slate-400">
                  These will be delivered the next time your device checks in.
                </p>
              </div>
            )}
          </div>
        </InfoCard>
      </div>
    </div>
  );
}
